import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const WeeklyAvailabilitySelector = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [weeklyHours, setWeeklyHours] = useState({
    sunday: { available: false, timeSlots: [] },
    monday: { available: true, timeSlots: [{ startTime: '09:00', endTime: '17:00', id: '1' }] },
    tuesday: { available: true, timeSlots: [{ startTime: '09:00', endTime: '17:00', id: '2' }] },
    wednesday: { available: true, timeSlots: [{ startTime: '09:00', endTime: '17:00', id: '3' }] },
    thursday: { available: true, timeSlots: [{ startTime: '09:00', endTime: '17:00', id: '4' }] },
    friday: { available: true, timeSlots: [{ startTime: '09:00', endTime: '17:00', id: '5' }] },
    saturday: { available: false, timeSlots: [] }
  });
  const [timezone, setTimezone] = useState('America/New_York');
  const [oneOffAvailability, setOneOffAvailability] = useState([]);
  const [activeTab, setActiveTab] = useState('recurring');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const daysOfWeek = [
    { key: 'sunday', label: 'Sunday', abbreviation: 'S' },
    { key: 'monday', label: 'Monday', abbreviation: 'M' },
    { key: 'tuesday', label: 'Tuesday', abbreviation: 'T' },
    { key: 'wednesday', label: 'Wednesday', abbreviation: 'W' },
    { key: 'thursday', label: 'Thursday', abbreviation: 'T' },
    { key: 'friday', label: 'Friday', abbreviation: 'F' },
    { key: 'saturday', label: 'Saturday', abbreviation: 'S' }
  ];

  // Generate time options in 15-minute increments
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayMinute = minute.toString().padStart(2, '0');
        const displayTime = `${displayHour}:${displayMinute} ${ampm}`;
        options.push({ value: timeValue, label: displayTime });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  useEffect(() => {
    if (currentUser) {
      loadWeeklyAvailability();
    }
  }, [currentUser]);

  const loadWeeklyAvailability = async () => {
    try {
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const userData = docSnap.data();
        
        // Load timezone
        if (userData.timezone) {
          setTimezone(userData.timezone);
        }
        
        // Load one-off availability
        setOneOffAvailability(userData.oneOffAvailability || []);
        
        // Convert old format to new format if needed
        if (userData.weeklyAvailability) {
          setWeeklyHours(userData.weeklyAvailability);
        } else if (userData.recurringAvailability) {
          // Convert from old format
          const convertedHours = convertOldToNewFormat(userData.recurringAvailability);
          setWeeklyHours(convertedHours);
        }
      }
    } catch (error) {
      console.error('Error loading weekly availability:', error);
    }
  };

  const convertOldToNewFormat = (oldAvailability) => {
    const newFormat = {
      sunday: { available: false, timeSlots: [] },
      monday: { available: false, timeSlots: [] },
      tuesday: { available: false, timeSlots: [] },
      wednesday: { available: false, timeSlots: [] },
      thursday: { available: false, timeSlots: [] },
      friday: { available: false, timeSlots: [] },
      saturday: { available: false, timeSlots: [] }
    };

    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    oldAvailability.forEach(slot => {
      if (slot.isActive) {
        const dayKey = dayMap[slot.dayOfWeek];
        if (dayKey) {
          newFormat[dayKey].available = true;
          newFormat[dayKey].timeSlots.push({
            id: slot.id || Date.now().toString(),
            startTime: slot.startTime,
            endTime: slot.endTime
          });
        }
      }
    });

    return newFormat;
  };

  // Convert weeklyAvailability back to recurringAvailability format for backward compatibility
  const convertWeeklyToRecurringForSave = (weeklyAvailability) => {
    const recurringSlots = [];
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    dayMap.forEach((dayKey, dayOfWeek) => {
      const dayData = weeklyAvailability[dayKey];
      if (dayData && dayData.available) {
        dayData.timeSlots.forEach(slot => {
          recurringSlots.push({
            id: slot.id,
            dayOfWeek: dayOfWeek,
            startTime: slot.startTime,
            endTime: slot.endTime,
            isActive: true
          });
        });
      }
    });
    
    return recurringSlots;
  };

  const saveWeeklyAvailability = async () => {
    setLoading(true);
    try {
      // Save both formats for backward compatibility
      const recurringFormat = convertWeeklyToRecurringForSave(weeklyHours);
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        weeklyAvailability: weeklyHours,
        recurringAvailability: recurringFormat, // Keep for backward compatibility
        oneOffAvailability: oneOffAvailability,
        lastAvailabilityUpdate: new Date()
      });
      setSuccessMessage('Availability saved successfully!');
      setErrorMessage('');
      
      // Clear the success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error saving availability:', error);
      setErrorMessage('Error saving availability. Please try again.');
      setSuccessMessage('');
      
      // Clear the error message after 8 seconds
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  const toggleDayAvailability = (dayKey) => {
    setWeeklyHours(prev => {
      const newHours = { ...prev };
      // Create a proper copy of the day object
      newHours[dayKey] = { ...prev[dayKey] };
      newHours[dayKey].available = !newHours[dayKey].available;
      
      // If turning on availability and no time slots exist, add default
      if (newHours[dayKey].available && newHours[dayKey].timeSlots.length === 0) {
        newHours[dayKey].timeSlots = [{ 
          id: Date.now().toString(), 
          startTime: '09:00', 
          endTime: '17:00' 
        }];
      }
      
      return newHours;
    });
  };

  const addTimeSlot = (dayKey) => {
    setWeeklyHours(prev => {
      const newHours = { ...prev };
      newHours[dayKey] = { ...prev[dayKey] };
      const newSlot = {
        id: Date.now().toString(),
        startTime: '09:00',
        endTime: '17:00'
      };
      newHours[dayKey].timeSlots = [...newHours[dayKey].timeSlots, newSlot];
      return newHours;
    });
  };

  const updateTimeSlot = (dayKey, slotId, field, value) => {
    setWeeklyHours(prev => {
      const newHours = { ...prev };
      newHours[dayKey] = { ...prev[dayKey] };
      newHours[dayKey].timeSlots = newHours[dayKey].timeSlots.map(slot =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      );
      return newHours;
    });
  };

  const removeTimeSlot = (dayKey, slotId) => {
    setWeeklyHours(prev => {
      const newHours = { ...prev };
      newHours[dayKey] = { ...prev[dayKey] };
      newHours[dayKey].timeSlots = newHours[dayKey].timeSlots.filter(slot => slot.id !== slotId);
      
      // If no time slots left, mark day as unavailable
      if (newHours[dayKey].timeSlots.length === 0) {
        newHours[dayKey].available = false;
      }
      
      return newHours;
    });
  };

  // One-off Availability Functions
  const addOneOffSlot = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const newSlot = {
      id: Date.now().toString(),
      date: tomorrow.toISOString().split('T')[0],
      startTime: '09:00',
      endTime: '17:00',
      isActive: true
    };
    setOneOffAvailability(prev => [...prev, newSlot]);
  };

  const updateOneOffSlot = (id, field, value) => {
    setOneOffAvailability(prev =>
      prev.map(slot =>
        slot.id === id ? { ...slot, [field]: value } : slot
      )
    );
  };

  const removeOneOffSlot = (id) => {
    setOneOffAvailability(prev => prev.filter(slot => slot.id !== id));
  };

  const formatTimeForDisplay = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="weekly-availability-selector">
      <div className="availability-header">
        <h2>Manage Your Availability</h2>
        <p>Set your weekly schedule and specific dates when you're available for sessions</p>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}

      <div className="availability-tabs">
        <button 
          className={`tab-button ${activeTab === 'recurring' ? 'active' : ''}`}
          onClick={() => setActiveTab('recurring')}
        >
          Weekly Hours
        </button>
        <button 
          className={`tab-button ${activeTab === 'oneoff' ? 'active' : ''}`}
          onClick={() => setActiveTab('oneoff')}
        >
          Date-specific hours
        </button>
      </div>

      <div className="timezone-selector">
        <div className="timezone-display">
          <span className="timezone-label">🌍 {timezone.replace('_', ' ')}</span>
        </div>
      </div>

      {activeTab === 'recurring' && (
        <div className="recurring-availability">
          <div className="weekly-hours-container">
            {/* Left Column - Days and Time Settings */}
            <div className="weekly-hours-left">
              <div className="days-container">
                {daysOfWeek.map(day => (
                  <div key={day.key} className="day-row">
                    <div className="day-header">
                      <div className="day-info">
                        <div className="day-circle">
                          <span className="day-abbreviation">{day.abbreviation}</span>
                        </div>
                        <span className="day-label">{day.label}</span>
                      </div>
                      
                      <div className="day-toggle">
                        {weeklyHours[day.key].available ? (
                          <button 
                            onClick={() => toggleDayAvailability(day.key)}
                            className="unavailable-btn"
                          >
                            Unavailable
                          </button>
                        ) : (
                          <button 
                            onClick={() => toggleDayAvailability(day.key)}
                            className="unavailable-btn inactive"
                          >
                            Unavailable
                          </button>
                        )}
                      </div>
                    </div>

                    {weeklyHours[day.key].available ? (
                      <div className="time-slots-container">
                        {weeklyHours[day.key].timeSlots.map((slot, index) => (
                          <div key={slot.id} className="time-slot-row">
                            <div className="time-inputs">
                              <select
                                value={slot.startTime}
                                onChange={(e) => updateTimeSlot(day.key, slot.id, 'startTime', e.target.value)}
                                className="time-select"
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <span className="time-separator">-</span>
                              <select
                                value={slot.endTime}
                                onChange={(e) => updateTimeSlot(day.key, slot.id, 'endTime', e.target.value)}
                                className="time-select"
                              >
                                {timeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="slot-actions">
                              {weeklyHours[day.key].timeSlots.length > 1 && (
                                <button
                                  onClick={() => removeTimeSlot(day.key, slot.id)}
                                  className="remove-slot-btn"
                                  title="Remove time slot"
                                >
                                  ×
                                </button>
                              )}
                              
                              {index === weeklyHours[day.key].timeSlots.length - 1 && (
                                <button
                                  onClick={() => addTimeSlot(day.key)}
                                  className="add-slot-btn"
                                  title="Add time slot"
                                >
                                  +
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="unavailable-day-actions">
                        <button
                          onClick={() => toggleDayAvailability(day.key)}
                          className="add-hours-btn"
                        >
                          + Add hours
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Summary and Save */}
            <div className="weekly-hours-right">
              <div className="availability-summary">
                <h3>Summary</h3>
                <div className="summary-list">
                  {daysOfWeek.map(day => {
                    const dayData = weeklyHours[day.key];
                    return (
                      <div key={day.key} className="summary-item">
                        <span className="summary-day">{day.label}:</span>
                        {dayData.available ? (
                          <div className="summary-times">
                            {dayData.timeSlots.map((slot, index) => (
                              <span key={slot.id} className="summary-time">
                                {formatTimeForDisplay(slot.startTime)} - {formatTimeForDisplay(slot.endTime)}
                                {index < dayData.timeSlots.length - 1 && ', '}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="summary-unavailable">Unavailable</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="availability-actions">
                <button 
                  onClick={saveWeeklyAvailability} 
                  disabled={loading}
                  className="save-availability-btn"
                >
                  {loading ? 'Saving...' : 'Save Availability'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'oneoff' && (
        <div className="oneoff-availability">
          <div className="section-header">
            <h3>Date-specific hours</h3>
            <p>Adjust hours for specific days</p>
            <button onClick={addOneOffSlot} className="add-slot-btn-primary">
              + Add date-specific hours
            </button>
          </div>

          <div className="availability-slots">
            {oneOffAvailability.length === 0 ? (
              <div className="no-slots">
                <p>No date-specific availability set. Click "Add date-specific hours" to get started.</p>
              </div>
            ) : (
              oneOffAvailability.map(slot => (
                <div key={slot.id} className="availability-slot">
                  <div className="slot-controls">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={slot.isActive}
                        onChange={(e) => updateOneOffSlot(slot.id, 'isActive', e.target.checked)}
                      />
                      <span className="toggle-text">Active</span>
                    </label>
                  </div>

                  <div className="slot-config">
                    <div className="date-selector">
                      <label>Date:</label>
                      <input
                        type="date"
                        value={slot.date}
                        onChange={(e) => updateOneOffSlot(slot.id, 'date', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="time-range">
                      <label>From:</label>
                      <select
                        value={slot.startTime}
                        onChange={(e) => updateOneOffSlot(slot.id, 'startTime', e.target.value)}
                        className="time-select"
                      >
                        {timeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="time-range">
                      <label>To:</label>
                      <select
                        value={slot.endTime}
                        onChange={(e) => updateOneOffSlot(slot.id, 'endTime', e.target.value)}
                        className="time-select"
                      >
                        {timeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="slot-preview">
                      {new Date(slot.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                      {' '}{formatTimeForDisplay(slot.startTime)} - {formatTimeForDisplay(slot.endTime)}
                    </div>

                    <button 
                      onClick={() => removeOneOffSlot(slot.id)}
                      className="remove-slot-btn-large"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="availability-actions">
        <button 
          onClick={saveWeeklyAvailability} 
          disabled={loading}
          className="save-availability-btn"
        >
          {loading ? 'Saving...' : 'Save Availability'}
        </button>
      </div>
    </div>
  );
};

export default WeeklyAvailabilitySelector;